import classNames from "classnames";
import useToastContext from "hooks/useToastContext";
import { isEmpty } from "lodash";
import { BiCheckCircle, BiXCircle } from "react-icons/bi";
import { TiDelete, TiPencil } from "react-icons/ti";
import { apiPut } from "utils/apiFetch";
import apiPath from "utils/apiPath";

export default function QuestionsView({ questions, onQuestionsChanged }) {
  const notification = useToastContext();
  async function removeQuestionHandler(id) {
    try {
      const path = apiPath.quizQuestions + "/" + id;
      const result = await apiPut(path, {
        testId: "",
      });
      if (result?.status === 200) {
        notification.success("Question removed successfully");
        onQuestionsChanged && onQuestionsChanged();
      }
    } catch (error) {
      console.log(error);
      notification.error(error?.response?.data?.message);
    }
  }

  if (isEmpty(questions) || questions?.length === 0 || !questions) return null;
  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-xl font-bold">Questions Information</h2>
      {questions &&
        questions.map((question, index) => {
          return (
            <div
              className="border rounded-md shadow-lg p-2 flex flex-col gap-4 overflow-hidden"
              key={question._id}
            >
              <div className="font-bold bg-gray-200 px-2 py-3 m-[-8px] rounded-md flex items-center justify-between">
                <h3>Question {index + 1}</h3>
                <div className="flex items-center gap-2 me-2 flex-wrap">
                  <button
                    type="button"
                    className="bg-red-500 rounded-full p-2 px-3 hover:bg-red-400 duration-300 flex items-center gap-2 text-white text-xs"
                    onClick={() => removeQuestionHandler(question._id)}
                  >
                    <TiDelete size={16} color="white" /> Remove From Test
                  </button>
                  <button
                    type="button"
                    className="bg-blue-500 rounded-full p-2 px-3 hover:bg-blue-400 duration-300 flex items-center gap-2 text-white text-xs"
                  >
                    <TiPencil size={16} color="white" /> Edit Question
                  </button>
                </div>
              </div>
              <div className="flex flex-col gap-2 *:py-3">
                <div className="flex-1 flex  gap-2 border-b">
                  <div className="flex flex-col flex-1 gap-2">
                    <p className="font-bold">Title: </p>
                    <p>{question.title}</p>
                  </div>
                  <div className="flex flex-col flex-1 gap-2">
                    <p className="font-bold">Title (Arabic): </p>
                    <p>{question.titleAr}</p>
                  </div>
                </div>
                <div className="flex-1 flex gap-2 border-b">
                  <div className="flex flex-col flex-1 gap-2">
                    <p className="font-bold">Points: </p>
                    <p>{question.points}</p>
                  </div>
                  <div className="flex flex-col flex-1 gap-2">
                    <p className="font-bold">Type: </p>
                    <p>{getQuestionType(question.type)}</p>
                  </div>
                </div>
                {question.type === "single" || question.type === "multiple" ? (
                  <OptionsViewTable options={question.options} />
                ) : null}
                {question.type === "truefalse" ? (
                  <div className="flex-1 flex  gap-2 border-b">
                    <p className="font-bold">Is the statement true? </p>
                    <p>
                      {question.isTrue ? (
                        <span className="flex items-center gap-2">
                          <BiCheckCircle color="green" size={20} /> Yes
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <BiXCircle color="red" size={20} /> No
                        </span>
                      )}
                    </p>
                  </div>
                ) : null}
                {question.type === "essay" ? (
                  <div className="flex-1 flex  gap-2 ">
                    <p className="font-bold">Expected Answer: </p>
                    <p>{question.expectedAnswer}</p>
                  </div>
                ) : null}
                {question.type === "matching" ? (
                  <MatchingPairsViewTable
                    matchingPairs={question.matchingPairs}
                  />
                ) : null}
              </div>
            </div>
          );
        })}
    </section>
  );
}

function OptionsViewTable({ options }) {
  if (isEmpty(options) || options?.length === 0 || !options) return null;
  return (
    <div className="flex-1 flex flex-col  gap-2">
      <p className="font-bold">Options:</p>
      <table>
        <thead>
          <tr>
            <th className="py-3 px-6">#</th>
            <th className="py-3 px-6">Title</th>
            <th className="py-3 px-6">Title (Arabic)</th>
            <th className="py-3 px-6">Is Correct</th>
          </tr>
        </thead>
        <tbody>
          {options?.map((option, index) => {
            return (
              <tr className="border-b " key={option._id}>
                <td className="py-3 px-6 border-l text-center">{index + 1}</td>
                <td className="py-3 px-6 border-l">{option.text}</td>
                <td className="py-3 px-6 border-l">{option.textAr}</td>
                <td
                  className={classNames(
                    "py-3 px-6 border-l font-bold flex items-center gap-2",
                    option.isCorrect ? "text-green-500" : "text-red-500"
                  )}
                >
                  {option.isCorrect ? (
                    <>
                      <BiCheckCircle color="green" size={20} /> Correct
                    </>
                  ) : (
                    <>
                      <BiXCircle color="red" size={20} /> Incorrect
                    </>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function MatchingPairsViewTable({ matchingPairs }) {
  if (isEmpty(matchingPairs) || matchingPairs?.length === 0 || !matchingPairs)
    return null;
  return (
    <div className="flex-1 flex flex-col  gap-2 ">
      <p className="font-bold">Matching Pairs:</p>
      <table>
        <thead>
          <tr>
            <th className="py-3 px-6">#</th>
            <th className="py-3 px-6">Left / Left (Arabic)</th>
            <th className="py-3 px-6">Right / Right (Arabic)</th>
          </tr>
        </thead>
        <tbody>
          {matchingPairs?.map((pair, index) => {
            return (
              <tr className="border-b " key={pair._id}>
                <td className="py-3 px-6 border-l text-center">{index + 1}</td>
                <td className="py-3 px-6 border-l">
                  {pair.left.text} / {pair.left.textAr}
                </td>
                <td className="py-3 px-6 border-l">
                  {pair.right.text} / {pair.right.textAr}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function getQuestionType(type) {
  if (type === "single") return "Multiple Choice - Single Answer";
  if (type === "multiple") return "Multiple Choice - Multiple Answers";
  if (type === "truefalse") return "True or False Question";
  if (type === "essay") return "Essay Question";
  if (type === "matching") return "Matching Question";
  return "";
}
